import { RelativeComponentLoader } from "../../../../components/loader";
import { AutoManageGraph } from "../../AutoManageGraph";
import type { TopInfo } from "../../types";
import type { WarehouseAutoSavingsGraphItem } from "../../types";
import { EmptyScreen } from "../../misc";
import { NativeSelect, Stack } from "@altimateai/lego";
import styles from "../../styles.module.scss";
import NoAutoSave from "./NoAutoSave";

interface AutoManageSectionProps {
  data: {
    graph_data?: WarehouseAutoSavingsGraphItem[];
    top_info?: unknown;
  } | null;
  isLoading: boolean;
  startDate: Date;
  endDate: Date;
  autoManageStatus: boolean;
  warehouseRK: string;
  estimatedSavings: number;
  aggregation: string;
  setAggregation: (value: string) => void;
  resizingEnabled?: boolean;
}

function isTopInfoRecord(value: unknown): value is Record<string, TopInfo> {
  return typeof value === "object" && value !== null;
}

const AutoManageSection = ({
  data,
  isLoading,
  startDate: _startDate,
  endDate: _endDate,
  autoManageStatus,
  warehouseRK: _warehouseRK,
  estimatedSavings,
  aggregation,
  setAggregation,
  resizingEnabled = false,
}: AutoManageSectionProps) => {
  const timeFrames = [
    { label: "Week", value: 7 },
    { label: "Last 30 Days", value: 30 },
  ];

  const AggregationOptions = [
    { label: "Daily", value: "day" },
    { label: "Weekly", value: "week" },
    { label: "Monthly", value: "month" },
  ];

  if (data?.graph_data && data?.graph_data.length === 0)
    return (
      <EmptyScreen
        title="Graph not available"
        subTitle="No data available for the selected period."
      />
    );

  return (
    <Stack gap={"md"} direction={"column"}>
      {!autoManageStatus && <NoAutoSave estimatedSavings={estimatedSavings} />}
      {isLoading && <RelativeComponentLoader label="Loading..." componentHeight={40} />}
      {data && (
        <Stack direction={"column"}>
          <Stack align={"center"} justify={"between"}>
            <h4>Auto Tune Savings</h4>
            <Stack align={"center"} gap={"sm"}>
              <NativeSelect
                value={aggregation}
                onChange={(value: unknown) => setAggregation(value as string)}
                className="al-w-[150px] al-bg-white al-text-brand"
                options={AggregationOptions.map(op => ({
                  label: op.label,
                  value: op.value,
                }))}
                buttonProps={{ "aria-label": "Aggregation" }}
              />
            </Stack>
          </Stack>
          <div className={styles.warehouse_graph}>
            <AutoManageGraph
              topInfo={isTopInfoRecord(data?.top_info) ? data.top_info : {}}
              data={data?.graph_data}
              defaultTimeFrame={timeFrames}
              resizingEnabled={resizingEnabled}
            />
          </div>
        </Stack>
      )}
    </Stack>
  );
};

export default AutoManageSection;
